import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { fileUrl } from "../lib/api";
import { usePrimaryPropertyId } from "../hooks/useMyProperties";
import { Loader, SectionHeading, EmptyState } from "../components/Loader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { fmtDate, relativeDays } from "../lib/format";
import { warrantyState, StatusPill } from "../components/StatusPill";
import { Home, Search, Boxes, Palette } from "lucide-react";

const FIELD_LABELS = {
  manufacturer: "Manufacturer", model: "Model", serial: "Serial Number", install_date: "Installation Date",
  installer: "Installer", contractor: "Contractor / Vendor", warranty_start: "Warranty Start",
  warranty_expiration: "Warranty Expiration", maintenance: "Maintenance", location: "Location in Home",
  product: "Product", color: "Color", color_code: "Color Code", finish: "Finish", supplier: "Supplier", notes: "Notes",
};
const ORDER = ["manufacturer", "product", "model", "serial", "color", "color_code", "finish", "location",
  "install_date", "installer", "contractor", "supplier", "warranty_start", "warranty_expiration", "maintenance", "notes"];

function RecordCard({ r, onOpen }) {
  const img = (r.photos && r.photos[0]) ? (r.photos[0].startsWith("http") ? r.photos[0] : fileUrl(r.photos[0])) : null;
  return (
    <button onClick={() => onOpen(r)} className="text-left bg-arq-card border border-border rounded-md overflow-hidden hover:border-arq-green transition-colors group" data-testid={`record-${r.id}`}>
      {img && <div className="aspect-[3/2] overflow-hidden bg-arq-surface"><img src={img} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
      <div className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-arq-accent font-semibold">{r.category}</p>
        <p className="font-medium text-arq-ink mt-0.5 leading-snug">{r.name || r.product}</p>
        <p className="text-xs text-muted-foreground mt-1">{r.manufacturer} {r.model ? `· ${r.model}` : ""}</p>
        {r.location && <p className="text-xs text-muted-foreground mt-0.5">{r.location}</p>}
      </div>
    </button>
  );
}

export default function MyHome() {
  const { propertyId, isLoading } = usePrimaryPropertyId();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const { data: records = [], isLoading: rl } = useQuery({
    queryKey: ["home-records", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}/home-records`)).data, enabled: !!propertyId });
  const { data: prop } = useQuery({ queryKey: ["property", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}`)).data, enabled: !!propertyId });

  const filter = (kind) => records.filter((r) => r.kind === kind && (!q ||
    JSON.stringify(r).toLowerCase().includes(q.toLowerCase())));

  const components = useMemo(() => filter("component"), [records, q]);
  const finishes = useMemo(() => filter("finish"), [records, q]);

  if (isLoading || rl) return <Loader label="Loading your home record" />;

  return (
    <div className="space-y-6 arq-fade-up">
      <SectionHeading overline="Own" title="My Home">
        <div className="relative hidden sm:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search records…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-64" data-testid="myhome-search" />
        </div>
      </SectionHeading>

      {prop && (
        <div className="bg-arq-card border border-border rounded-md p-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="property-overview">
          {[["Model", prop.model], ["Floor Plan", prop.floor_plan], ["Square Feet", prop.square_footage?.toLocaleString()],
            ["Bedrooms", prop.bedrooms], ["Bathrooms", prop.bathrooms], ["Garage", `${prop.garage_spaces} cars`],
            ["Lot Size", prop.lot_size], ["Community", prop.community]].map(([k, v]) => (
            <div key={k}><p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p><p className="text-sm font-medium text-arq-ink mt-0.5">{v || "—"}</p></div>
          ))}
        </div>
      )}

      <div className="relative sm:hidden">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search records…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-full" />
      </div>

      <Tabs defaultValue="systems">
        <TabsList>
          <TabsTrigger value="systems" data-testid="tab-systems"><Boxes className="h-4 w-4 mr-1.5" />Systems & Equipment</TabsTrigger>
          <TabsTrigger value="finishes" data-testid="tab-finishes"><Palette className="h-4 w-4 mr-1.5" />Finish Selections</TabsTrigger>
        </TabsList>
        <TabsContent value="systems" className="mt-6">
          {components.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{components.map((r) => <RecordCard key={r.id} r={r} onOpen={setActive} />)}</div>
            : <EmptyState icon={Home} title="No equipment records" />}
        </TabsContent>
        <TabsContent value="finishes" className="mt-6">
          {finishes.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{finishes.map((r) => <RecordCard key={r.id} r={r} onOpen={setActive} />)}</div>
            : <EmptyState icon={Palette} title="No finish selections" />}
        </TabsContent>
      </Tabs>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-lg" data-testid="record-detail">
          {active && (
            <>
              <DialogHeader>
                <p className="text-[10px] uppercase tracking-widest text-arq-accent font-semibold">{active.category}</p>
                <DialogTitle className="font-display text-2xl">{active.name || active.product}</DialogTitle>
              </DialogHeader>
              {active.photos && active.photos[0] && (
                <img src={active.photos[0].startsWith("http") ? active.photos[0] : fileUrl(active.photos[0])} alt="" className="w-full aspect-video object-cover rounded-md" />
              )}
              <dl className="divide-y divide-border">
                {ORDER.filter((k) => active[k]).map((k) => (
                  <div key={k} className="flex justify-between gap-4 py-2">
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">{FIELD_LABELS[k]}</dt>
                    <dd className="text-sm text-arq-ink text-right">{k.includes("date") || k.includes("_start") || k.includes("expiration") ? fmtDate(active[k]) : active[k]}</dd>
                  </div>
                ))}
              </dl>
              {active.warranty_expiration && (
                <div className="flex items-center gap-2 text-sm">
                  <StatusPill status={warrantyState(active.warranty_expiration)} />
                  <span className="text-muted-foreground">Warranty expires {relativeDays(active.warranty_expiration)}</span>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
